import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Button, Input, Label } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { toast } from "react-hot-toast";
import { TableGroup, tableOrderService } from "@/api/services/table-order.service";
import { SITE_ID } from "@/assets/meta/meta";

const TableForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isCreate = useMemo(() => id === "0", [id]);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [groups, setGroups] = useState<TableGroup[]>([]);
  const [groupId, setGroupId] = useState("");
  const [capacity, setCapacity] = useState(4);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      try {
        const groupResponse = await tableOrderService.listAllTableGroups();
        setGroups(groupResponse.results || []);

        if (isCreate || !id) {
          return;
        }

        const response = await tableOrderService.getServiceTable(Number(id));
        const data = response.data;
        setCode(data.code || "");
        setName(data.name || "");
        setGroupId(data.group ? String(data.group) : "");
        setCapacity(data.capacity || 1);
        setDisplayOrder(data.display_order || 0);
        setIsActive(Boolean(data.is_active));
      } catch (error) {
        toast.error(isCreate ? "테이블 폼 정보를 불러오지 못했습니다." : "테이블 정보를 불러오지 못했습니다.");
        navigate("/table-order/tables");
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [id, isCreate, navigate]);

  const handleSubmit = async () => {
    if (!code.trim()) {
      toast.error("코드를 입력해주세요.");
      return;
    }
    if (!name.trim()) {
      toast.error("이름을 입력해주세요.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...(Number.isFinite(SITE_ID) ? { site: SITE_ID } : {}),
        code: code.trim(),
        name: name.trim(),
        group: groupId ? Number(groupId) : null,
        capacity,
        display_order: displayOrder,
        is_active: isActive,
      };

      if (isCreate) {
        await tableOrderService.createServiceTable(payload);
        toast.success("테이블이 생성되었습니다.");
      } else {
        await tableOrderService.updateServiceTable(Number(id), payload);
        toast.success("테이블이 수정되었습니다.");
      }

      navigate("/table-order/tables");
    } catch (error) {
      toast.error("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={isCreate ? "테이블 생성" : "테이블 수정"} pageTitle="현장주문" />

        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">{isCreate ? "테이블 생성" : `테이블 수정 #${id}`}</h5>
            <div className="d-flex gap-2">
              <Button color="light" onClick={() => navigate("/table-order/tables")}>목록</Button>
              <Button color="primary" onClick={handleSubmit} disabled={saving || loading}>
                {saving ? "저장 중..." : "저장"}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <p className="text-muted mb-0">불러오는 중...</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="form-label">코드</Label>
                  <Input value={code} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)} />
                </div>
                <div>
                  <Label className="form-label">이름</Label>
                  <Input value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
                </div>
                <div>
                  <Label className="form-label">테이블 그룹</Label>
                  <select className="form-select" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                    <option value="">미지정</option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="form-label">수용 인원</Label>
                  <Input type="number" value={capacity} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCapacity(Number(e.target.value || 1))} />
                </div>
                <div>
                  <Label className="form-label">정렬 순서</Label>
                  <Input type="number" value={displayOrder} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayOrder(Number(e.target.value || 0))} />
                </div>
                <div>
                  <Label className="form-label">활성 여부</Label>
                  <select className="form-select" value={isActive ? "true" : "false"} onChange={(e) => setIsActive(e.target.value === "true")}>
                    <option value="true">활성</option>
                    <option value="false">비활성</option>
                  </select>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default TableForm;
